import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, NAV_LINKS } from "@/lib/brand";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  // On non-home pages the nav always sits on ivory, so use solid styling.
  const solid = scrolled || !isHome || open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  const handleSection = (e, section) => {
    e.preventDefault();
    setOpen(false);
    if (!isHome) {
      navigate("/#" + section);
      return;
    }
    const scroll = () => {
      const el = document.getElementById(section);
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: "smooth" });
    };
    setTimeout(scroll, 60);
  };

  const handleNav = (e, link) => {
    if (link.section) handleSection(e, link.section);
  };

  const textColor = solid ? "text-brown" : "text-ivory";

  return (
    <header
      data-testid="navbar-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        solid ? "bg-ivory/90 backdrop-blur-md shadow-[0_2px_20px_rgba(74,49,37,0.06)]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[60px] md:h-[72px]">
          {/* Left: logo */}
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-3 shrink-0"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <img
              src="/indiabode-logo.jpg"
              alt="IndiAbode logo"
              className="h-9 w-9 md:h-10 md:w-10 rounded-full object-cover ring-1 ring-gold/40"
            />
            <span className={`font-serif text-xl md:text-[22px] font-semibold tracking-tight ${textColor} transition-colors duration-500`}>
              IndiAbode
            </span>
          </Link>

          {/* Center: menu (desktop) */}
          <div className="hidden md:flex items-center gap-9 absolute left-1/2 -translate-x-1/2">
            {NAV_LINKS.map((link) =>
              link.section ? (
                <a
                  key={link.label}
                  href={link.to}
                  data-testid={`navlink-${link.section}`}
                  onClick={(e) => handleNav(e, link)}
                  className={`text-sm font-medium tracking-wide ${textColor} hover:text-terracotta transition-colors duration-300`}
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  data-testid={`navlink-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium tracking-wide ${textColor} hover:text-terracotta transition-colors duration-300`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Right: Call Now (desktop) */}
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="navbar-call-now"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-medium text-white hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-300"
          >
            <Phone className="h-4 w-4" /> Call Now
          </a>

          {/* Mobile hamburger */}
          <button
            data-testid="navbar-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            className={`md:hidden inline-flex items-center justify-center h-11 w-11 rounded-full ${textColor}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-ivory border-t border-gold/20 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) =>
                link.section ? (
                  <a
                    key={link.label}
                    href={link.to}
                    data-testid={`mobile-navlink-${link.section}`}
                    onClick={(e) => handleNav(e, link)}
                    className="py-3 text-lg font-medium text-brown border-b border-gold/10"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    data-testid={`mobile-navlink-${link.label.toLowerCase()}`}
                    className="py-3 text-lg font-medium text-brown border-b border-gold/10"
                  >
                    {link.label}
                  </Link>
                )
              )}
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="mobile-call-now"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-5 py-3.5 text-base font-medium text-white"
              >
                <Phone className="h-4 w-4" /> Call Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
